import React, { useEffect, useState } from "react";

import {
  getAdminVerificationYearlyDataYear,
  getContactLink,
  goTo,
  useGetAllUserVerificationFarmTasks,
  useGetCarbonProgramByFarmId,
  useGetFarmDetails,
  useGetFarmPayments,
  useLocation,
  useTranslation,
  useUserContext,
} from "utils";

import { CURRENCY_CODE, VESTING_PERCENTAGE } from "consts";

import { ACCOUNTING_METHODOLOGY } from "models";

import {
  Col,
  EmptyStatePanel,
  ErrorBoundary,
  InformationCard,
  MainPage,
  PageSpin,
  PayoutImpactCard,
  Row,
  TaskAccessCard,
  Typography,
  WalletOverviewSwitch,
} from "components";

import { PaymentInfo } from "pages/app/farm/components/PaymentInfo";
import { TaskOverviewDrawer } from "pages/app/farm/components/TaskOverviewDrawer";
import { PastPayments } from "pages/app/wallet/PastPayments.tsx";

export const Wallet = () => {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const taskDrawer = params.get("openTaskDrawer");

  const { t } = useTranslation(["points", "modules"]);
  const { user } = useUserContext();
  const { data: farm } = useGetFarmDetails({ farmId: user?.farm?.id ?? "" });
  const { data: farmPayments, isLoading: isLoadingFarmPayments } = useGetFarmPayments(true);
  const { data: farmTasks, isLoading: loadFarmTasks, refetch: refetchFarmTasks } = useGetAllUserVerificationFarmTasks();
  const { data: carbonProgram } = useGetCarbonProgramByFarmId(farm?.id);

  const [showTaskOverviewDrawer, setShowTaskOverviewDrawer] = useState(false);
  const [activeTabIndex, setActiveTabIndex] = useState(0);

  // In prior iterations the payout year was dependant on the reporting year, but I think it makes more sense to depend it on the verification year
  const verificationYear = getAdminVerificationYearlyDataYear();

  // If the user has already received a payment for the current payout year (leading to there being no unpaid payment items)
  // or if the farm was rejected we want to already jump to the next year

  const hasReceivedPayoutOfCurrentYear = farmPayments?.payments?.some(({ status, year }) => status === "paid" && year === verificationYear);
  const payoutYear = verificationYear + (hasReceivedPayoutOfCurrentYear ? 1 : 0);

  // Direct and future payouts section in the WalletOverviewSwitch
  const currentPayout = farmPayments?.payments?.reduce((acc, { amount, status, year }) => (status !== "paid" && year <= payoutYear ? acc + amount : acc), 0);

  const futurePayments = farmPayments?.payments?.filter(({ year }) => year > payoutYear) ?? [];
  const futurePayout = futurePayments?.reduce((acc, { amount }) => acc + amount, 0);

  useEffect(() => {
    if (taskDrawer) setShowTaskOverviewDrawer(true);
  }, [taskDrawer]);

  if (isLoadingFarmPayments) {
    return <PageSpin className="margin-top-200" fullHeight={false} />;
  }

  const navigationCrumbs = {
    crumbs: [{ name: user?.farm?.name || t("modules:navigation.overview.farm"), replacedByFarmSwitch: true }, { name: t("nav:railNav.farm.payout") }],
    additionalData: <></>,
  };

  const hasIncompleteTasks = farmTasks?.some(({ completedAt }) => !completedAt);

  return (
    <MainPage fullWidth pageName="wallet" navigationCrumbs={navigationCrumbs}>
      <ErrorBoundary>
        <div className="wallet-content-container">
          <Row className="margin-bottom-75 margin-top-75">
            <Col>
              <Typography variant="h1">{t("title")}</Typography>
            </Col>
          </Row>
        </div>

        <div className="wallet-content-container wallet-container">
          <Row justify="space-between">
            <Col lg={{ span: 8, order: 2 }}>
              {!farm?.bankingDetails ? (
                <EmptyStatePanel
                  icons={["tabler/wallet"]}
                  headline={t("paymentInfoMissing.headline")}
                  description={t("paymentInfoMissing.description")}
                  button={t("paymentInfoMissing.button")}
                  link={"/banking-details"}
                  infoLink={{
                    text: t("paymentInfoMissing.FAQ"),
                    onClick: () => goTo(null, null, getContactLink(), true),
                  }}
                  small
                  onClick={undefined}
                />
              ) : (
                <PaymentInfo farm={farm} t={t} />
              )}
            </Col>

            <Col lg={{ span: 14, order: 1 }}>
              <ErrorBoundary>
                {hasIncompleteTasks && (
                  <div className="row-margin-bottom">
                    <TaskAccessCard farmTasks={farmTasks} onClick={() => setShowTaskOverviewDrawer(true)} />
                  </div>
                )}
                <div className="row-margin-bottom">
                  <WalletOverviewSwitch
                    currentYear={payoutYear}
                    // Payout for current year is the sum of all unpaid items with year <= current year, for vesting > current year
                    payout={currentPayout ?? 0}
                    currency={carbonProgram?.currency ?? CURRENCY_CODE.EUR}
                    vestingPayout={futurePayout}
                    vestingYears={futurePayments?.map(({ year }: { year: number }) => year).sort((a, b) => a - b)}
                    hideVesting={carbonProgram?.accountingMethodology === ACCOUNTING_METHODOLOGY.inventory}
                    onSelect={setActiveTabIndex}
                    dataTestId="wallet-overview-switch"
                  />
                </div>
              </ErrorBoundary>
              {currentPayout === 0 && activeTabIndex === 0 && (
                <InformationCard
                  className="row-margin-bottom"
                  iconName="klim/funding"
                  title={t("noPayoutCard.title")}
                  text={t("noPayoutCard.text")}
                  variant="neutral"
                  dataTestId="no-current-payout-card"
                />
              )}
              {futurePayout === 0 && activeTabIndex === 1 && (
                <InformationCard
                  className="row-margin-bottom"
                  iconName="klim/funding"
                  title={t("noVestedPayoutCard.title")}
                  text={t("noVestedPayoutCard.text", { vestingPercentage: VESTING_PERCENTAGE * 100 })}
                  variant="neutral"
                  dataTestId="no-vested-payout-card"
                />
              )}
              {!!(currentPayout && currentPayout < 0 && activeTabIndex === 0) && (
                <InformationCard
                  className="row-margin-bottom"
                  iconName="klim/funding"
                  title={t("noPayoutObligationCard.title")}
                  text={t("noPayoutObligationCard.text")}
                  variant="neutral"
                  dataTestId="negative-payout-card"
                />
              )}
              <ErrorBoundary>
                <div key={farm?.id}>
                  {farmPayments?.payments?.map((payment, index) => {
                    if (activeTabIndex === 0 && payment.year === payoutYear && payment.status !== "paid") {
                      // This card corresponds to the current payout of the payoutYear (year in verification)
                      return (
                        <div key={`${payment.year}-${payment.amount}-${index}`}>
                          <PayoutImpactCard
                            deductedPayout={undefined}
                            payout={payment.amount - payment.includedVestingAmount}
                            year={payment.year}
                            currentYear={payoutYear}
                            icons={[{ icon: "tabler/wallet" }]}
                            className="margin-bottom-100"
                            dataTestId="wallet-payout-impact-card-payout-year"
                            isInVerification={payment.status === "inVerification"}
                            currency={carbonProgram?.currency}
                          />
                          {/* This card corresponds to the vested amount of the payoutYear (year in verification) (if it exists) */}
                          {payment.includedVestingAmount > 0 && (
                            <PayoutImpactCard
                              deductedPayout={payment.deductedAmount}
                              payout={payment.includedVestingAmount}
                              year={payment.year}
                              currentYear={payoutYear}
                              icons={[{ icon: "tabler/wallet" }]}
                              variant={"vesting"}
                              className="margin-bottom-100"
                              dataTestId="wallet-payout-impact-card-payout-year-vested"
                              currency={carbonProgram?.currency}
                            />
                          )}
                        </div>
                      );
                    }

                    if (activeTabIndex === 1 && payment.year > payoutYear) {
                      // This/those card(s) corresponds to the future payouts by year
                      return (
                        <PayoutImpactCard
                          deductedPayout={payment.deductedAmount}
                          payout={payment.amount}
                          year={payment.year}
                          currentYear={payoutYear}
                          icons={[{ icon: "tabler/wallet" }]}
                          variant={payment.type === "methods" ? "vestingMethod" : "vesting"}
                          key={`${payment.year}-${payment.includedVestingAmount}-${index}`}
                          className="margin-bottom-100"
                          dataTestId="wallet-payout-impact-card-future-years"
                          isInVerification={payment.status === "inVerification"}
                          currency={carbonProgram?.currency}
                        />
                      );
                    }
                    return null;
                  })}
                </div>
              </ErrorBoundary>
              <ErrorBoundary>
                <PastPayments farmPayments={farmPayments} currency={carbonProgram?.currency} t={t} payoutYear={payoutYear} />
              </ErrorBoundary>
            </Col>
          </Row>
        </div>
      </ErrorBoundary>
      <ErrorBoundary>
        <TaskOverviewDrawer
          farmTasks={farmTasks ?? []}
          visible={showTaskOverviewDrawer}
          onClose={() => setShowTaskOverviewDrawer(false)}
          onDataChange={refetchFarmTasks}
          loading={loadFarmTasks}
        />
      </ErrorBoundary>
    </MainPage>
  );
};
